import { useMemo, useState } from "react";
import { brandReports, categories } from "@/mocks/site";
import BrandModal from "@/pages/home/components/BrandModal";
import Reveal from "@/pages/home/components/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import type { BrandReport } from "@/pages/home/types";
import { categoryBadgeClass, riskBadgeClass, riskDotClass } from "@/pages/home/utils";

interface ArchiveSectionProps {
  keyword: string;
  activeCategory: string;
  onCategoryChange: (key: string) => void;
  onKeywordChange: (value: string) => void;
}

const riskFilters = [
  { key: "all", label: "全部风险" },
  { key: "高", label: "高风险" },
  { key: "中", label: "中风险" },
  { key: "低", label: "低风险" },
];

const tabs = [{ key: "all", label: "全部" }, ...categories.map((c) => ({ key: c.key, label: c.label }))];

export default function ArchiveSection({
  keyword,
  activeCategory,
  onCategoryChange,
  onKeywordChange,
}: ArchiveSectionProps) {
  const [riskLevel, setRiskLevel] = useState("all");
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<BrandReport | null>(null);

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const filtered = brandReports.filter((item) => {
      const matchCategory = activeCategory === "all" || item.category === activeCategory;
      const matchRisk = riskLevel === "all" || item.riskLevel === riskLevel;
      const matchKeyword =
        kw.length === 0 ||
        item.brandName.toLowerCase().includes(kw) ||
        item.industry.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.riskTags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchRisk && matchKeyword;
    });

    return [...filtered].sort((a, b) => (sortAsc ? a.reportCount - b.reportCount : b.reportCount - a.reportCount));
  }, [keyword, activeCategory, riskLevel, sortAsc]);

  const reset = () => {
    onKeywordChange("");
    onCategoryChange("all");
    setRiskLevel("all");
  };

  return (
    <section id="archive" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeading
          eyebrow="AVOIDANCE ARCHIVE"
          title="避雷档案库"
          desc="按类别、风险等级或关键词检索每一条争议记录。点击任意档案可查看投稿要点整理与对应的避坑建议。"
        />

        <div className="mt-8 flex flex-col gap-4">
          <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full flex-wrap gap-1 rounded-xl border border-background-200 bg-background-100 px-1 py-1 lg:w-auto lg:rounded-full">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onCategoryChange(tab.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-2 font-label text-sm transition-colors md:px-4 ${
                    activeCategory === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex w-full items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 lg:w-72">
              <i className="ri-search-2-line text-base leading-none text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => onKeywordChange(e.target.value)}
                placeholder="搜索品牌 / 问题关键词"
                aria-label="在档案库中搜索"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword ? (
                <button
                  type="button"
                  onClick={() => onKeywordChange("")}
                  aria-label="清除搜索"
                  className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-500 hover:text-foreground-800"
                >
                  <i className="ri-close-circle-line text-base leading-none"></i>
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {riskFilters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setRiskLevel(item.key)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 font-label text-xs transition-colors ${
                    riskLevel === item.key
                      ? "border-foreground-800 bg-foreground-900 text-background-50"
                      : "border-background-300 text-foreground-700 hover:border-foreground-500"
                  }`}
                >
                  {item.key !== "all" ? <span className={`h-1.5 w-1.5 rounded-full ${riskDotClass(item.key)}`}></span> : null}
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="font-label text-xs text-foreground-500">
                共 <strong className="font-heading text-base text-foreground-900">{results.length}</strong> 条档案
              </span>
              <button
                type="button"
                onClick={() => setSortAsc((v) => !v)}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-foreground-500"
              >
                <i className="ri-sort-desc text-base leading-none"></i>
                {sortAsc ? "记录数升序" : "记录数降序"}
              </button>
            </div>
          </div>
        </div>

        {results.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((item, index) => (
              <Reveal key={item.id} delay={(index % 3) * 80}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300">
                  <div className="flex items-start justify-between gap-3">
                    <span className={`rounded px-2 py-0.5 font-label text-xs ${categoryBadgeClass(item.category)}`}>
                      {item.categoryLabel}
                    </span>
                    <span className={`shrink-0 rounded-full border px-2.5 py-0.5 font-label text-xs ${riskBadgeClass(item.riskLevel)}`}>
                      {item.riskLevel}风险
                    </span>
                  </div>

                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.brandName}</h3>
                  <span className="mt-1 font-label text-xs text-foreground-500">{item.industry}</span>

                  <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {item.riskTags.slice(0, 3).map((tag) => (
                      <span key={tag} className="rounded bg-background-200 px-2 py-0.5 font-label text-xs text-foreground-700">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4">
                    <span className="font-label text-xs text-foreground-500">
                      记录 <strong className="font-heading text-sm text-foreground-900">{item.reportCount}</strong> 条
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelected(item)}
                      className="flex cursor-pointer items-center gap-1 whitespace-nowrap font-label text-sm text-primary-600 transition-colors hover:text-primary-700"
                    >
                      查看详情
                      <i className="ri-arrow-right-line text-base leading-none"></i>
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl leading-none"></i>
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">没有匹配到相关档案</p>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              换个关键词试试，或者放宽风险等级与分类筛选条件。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>

      <BrandModal report={selected} onClose={() => setSelected(null)} />
    </section>
  );
}